import { c as _c } from "react/compiler-runtime";
import { PlaceAutocomplete } from "@/components/others/place-autocomplete";
import { PlaceSuggestion } from "@/components/others/place-autocomplete/types";
import { trackHomeSearch } from "@/utils/analytics/track-search";
import { cn } from "@/utils/cn";
import { LocationPermissionDialog } from "@/components/location-permission-dialog";
import { Button } from "@/components/rac/button";
import { useLocationPermissionStore } from "@/stores/location-permission-store";
import { isLocationInBangalore } from "@/utils/location-utils";
import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfirmationDialog } from "./confirmation-dialog";
import { baseInputClasses } from "./input";
import type { SearchSectionProps } from "./types";
interface SearchFormProps extends SearchSectionProps {
  onSearchWithFallback: (suggestion: PlaceSuggestion) => void;
  isLoading: boolean;
  onSearchWithCoords?: (coords: {
    lat: number;
    long: number;
  }) => void;
}
const baseFormClasses = "flex flex-col md:flex-row gap-0 max-w-[746px] items-stretch md:items-start transform-gpu";
const baseButtonClasses = cn("text-lg font-medium h-[60px] md:h-[68px] lg:h-[72px] px-6", "rounded-none rounded-b-xl md:rounded-l-none md:rounded-r-xl border-2 border-l-0 md:border-l-0", "transition-colors duration-1000 ease-in-out transform-gpu active:scale-100 flex items-center justify-center", "w-full md:w-[140px] lg:w-[160px]");
export const SearchLocationForm: React.FC<SearchFormProps> = t0 => {
  const $ = _c(66);
  const {
    className: t1,
    onSearchWithFallback,
    isLoading,
    isLoggedIn: t2,
    onSearchWithCoords
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const isLoggedIn = t2 === undefined ? false : t2;
  const [suggestion, setSuggestion] = useState(null);
  const [activeDialog, setActiveDialog] = useState("none");
  const {
    getCurrentLocation,
    clearError,
    isGettingLocation,
    checkPermissionStatus
  } = useLocationPermissionStore();
  const [hasTransitioned, setHasTransitioned] = useState(false);
  const [displayAsLoggedIn, setDisplayAsLoggedIn] = useState(false);
  let t3;
  let t4;
  if ($[0] !== checkPermissionStatus) {
    t3 = () => {
      checkPermissionStatus();
    };
    t4 = [checkPermissionStatus];
    $[0] = checkPermissionStatus;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[3] !== hasTransitioned || $[4] !== isLoggedIn) {
    t5 = () => {
      if (!hasTransitioned && isLoggedIn) {
        const timer = setTimeout(() => {
          setDisplayAsLoggedIn(true);
          setHasTransitioned(true);
        }, 100);
        return () => clearTimeout(timer);
      } else {
        if (!isLoggedIn) {
          setDisplayAsLoggedIn(false);
          setHasTransitioned(false);
        }
      }
    };
    t6 = [isLoggedIn, hasTransitioned];
    $[3] = hasTransitioned;
    $[4] = isLoggedIn;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = suggestion_0 => {
      setSuggestion(suggestion_0);
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleSelectionMade = t7;
  let handleLocationReady;
  let handleSearchSubmit;
  if ($[8] !== getCurrentLocation || $[9] !== onSearchWithCoords || $[10] !== onSearchWithFallback || $[11] !== suggestion) {
    handleSearchSubmit = async e => {
      e.preventDefault();
      trackHomeSearch();
      if (suggestion) {
        onSearchWithFallback(suggestion);
        return;
      }
      const currentPermissionStatus = useLocationPermissionStore.getState().permissionStatus;
      if (currentPermissionStatus === "granted") {
        ;
        try {
          const position = await getCurrentLocation({
            enableHighAccuracy: true,
            timeout: 30000
          });
          handleLocationReady(position.coords);
        } catch (t8) {
          setActiveDialog("generalSearch");
        }
      } else {
        setActiveDialog("locationPermission");
      }
    };
    handleLocationReady = location => {
      if (!isLocationInBangalore(location)) {
        setActiveDialog("outsideBangalore");
        return;
      }
      const coords = {
        lat: location.latitude,
        long: location.longitude
      };
      onSearchWithCoords?.(coords);
      setActiveDialog("none");
    };
    $[8] = getCurrentLocation;
    $[9] = onSearchWithCoords;
    $[10] = onSearchWithFallback;
    $[11] = suggestion;
    $[12] = handleLocationReady;
    $[13] = handleSearchSubmit;
  } else {
    handleLocationReady = $[12];
    handleSearchSubmit = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setActiveDialog("generalSearch");
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleLocationPermissionDenied = t8;
  let t9;
  if ($[15] !== onSearchWithCoords) {
    t9 = () => {
      const bangaloreCenter = {
        latitude: 12.9716,
        longitude: 77.5946
      };
      const coords_0 = {
        lat: bangaloreCenter.latitude,
        long: bangaloreCenter.longitude
      };
      onSearchWithCoords?.(coords_0);
      setActiveDialog("none");
    };
    $[15] = onSearchWithCoords;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleUseBangaloreCenter = t9;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setActiveDialog("none");
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleStayOnCurrentScreen = t10;
  let t11;
  if ($[18] !== clearError) {
    t11 = () => {
      setActiveDialog("none");
      clearError();
    };
    $[18] = clearError;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const closeAllDialogs = t11;
  const isFormLoading = isLoading || isGettingLocation;
  const t12 = activeDialog === "locationPermission";
  let t13;
  if ($[20] !== closeAllDialogs) {
    t13 = open => !open && closeAllDialogs();
    $[20] = closeAllDialogs;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== handleLocationReady) {
    t14 = position_0 => {
      const coords_1 = {
        latitude: position_0.coords.latitude,
        longitude: position_0.coords.longitude
      };
      handleLocationReady(coords_1);
    };
    $[22] = handleLocationReady;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== closeAllDialogs || $[25] !== t12 || $[26] !== t13 || $[27] !== t14) {
    t15 = <LocationPermissionDialog open={t12} onOpenChange={t13} onPermissionGranted={t14} onPermissionDenied={handleLocationPermissionDenied} onCancel={closeAllDialogs} />;
    $[24] = closeAllDialogs;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = ["outsideBangalore", "generalSearch"];
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = t16.includes(activeDialog);
  let t18;
  if ($[30] !== closeAllDialogs) {
    t18 = open_0 => !open_0 && closeAllDialogs();
    $[30] = closeAllDialogs;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== handleUseBangaloreCenter || $[33] !== t17 || $[34] !== t18) {
    t19 = <ConfirmationDialog open={t17} onOpenChange={t18} onConfirm={handleUseBangaloreCenter} onCancel={handleStayOnCurrentScreen} isLoading={false} />;
    $[32] = handleUseBangaloreCenter;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== className) {
    t20 = cn(baseFormClasses, className);
    $[36] = className;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const t21 = displayAsLoggedIn ? "border-gray-300 focus-within:border-gray-400" : "border-primary-700 focus-within:border-primary-700";
  let t22;
  if ($[38] !== t21) {
    t22 = cn(baseInputClasses, t21);
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      "aria-label": "Search property by Locality or Project name",
      className: "border-0 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 rounded-none unfocusable",
      style: {
        boxShadow: "none",
        border: "none",
        outline: "none"
      }
    };
    t24 = {
      includedPrimaryTypes: ["locality", "sublocality", "establishment"],
      language: "en",
      region: "IN",
      radius: 30
    };
    $[40] = t23;
    $[41] = t24;
  } else {
    t23 = $[40];
    t24 = $[41];
  }
  let t25;
  if ($[42] !== t22) {
    t25 = <div className="flex-1"><PlaceAutocomplete onSelectionMade={handleSelectionMade} placeholder="Search Locality / Project name" className={t22} inputProps={t23} restrictions={t24} /></div>;
    $[42] = t22;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  const t26 = displayAsLoggedIn ? "bg-gray-200 text-gray-600 border-gray-300" : "bg-primary-700 text-primary-foreground border-primary-700";
  const t27 = isFormLoading && "data-[disabled]:opacity-100";
  let t28;
  if ($[44] !== t26 || $[45] !== t27) {
    t28 = cn(baseButtonClasses, t26, t27);
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== isFormLoading || $[48] !== isGettingLocation || $[49] !== isLoggedIn) {
    t29 = isFormLoading ? <><Loader2 className={`${isLoggedIn ? "w-4 h-4" : "w-5 h-5 md:w-6 md:h-6"} mr-2 animate-spin`} aria-hidden="true" /><span className="md:hidden lg:block">{isGettingLocation ? "Getting Location..." : "Loading..."}</span></> : <><Search className={`${isLoggedIn ? "w-4 h-4" : "w-5 h-5 md:w-6 md:h-6"} mr-2`} aria-hidden="true" /><span className="md:hidden lg:block">Search</span></>;
    $[47] = isFormLoading;
    $[48] = isGettingLocation;
    $[49] = isLoggedIn;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== isFormLoading || $[52] !== t28 || $[53] !== t29) {
    t30 = <Button type="submit" aria-label="Search properties" variant="bareshell" isDisabled={isFormLoading} className={t28}>{t29}</Button>;
    $[51] = isFormLoading;
    $[52] = t28;
    $[53] = t29;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== t20 || $[56] !== t25 || $[57] !== t30) {
    t31 = <div id="search-form" className={t20}>{t25}{t30}</div>;
    $[55] = t20;
    $[56] = t25;
    $[57] = t30;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== handleSearchSubmit || $[60] !== t31) {
    t32 = <form onSubmit={handleSearchSubmit}>{t31}</form>;
    $[59] = handleSearchSubmit;
    $[60] = t31;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t15 || $[63] !== t19 || $[64] !== t32) {
    t33 = <>{t15}{t19}{t32}</>;
    $[62] = t15;
    $[63] = t19;
    $[64] = t32;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  return t33;
};